import type { Chapter } from '@quranjs/api';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

interface ScheduledCard {
  pageNumber: number;
  due: string;
  state: number;
}

interface ReviewScheduleProps {
  dueNow: ScheduledCard[];
  upcoming: ScheduledCard[];
  chapters: Chapter[];
  onNavigate: (pageNumber: number) => void;
}

function getSurahName(pageNumber: number, chapters: Chapter[]): string {
  for (const chapter of chapters) {
    if (pageNumber >= chapter.pages[0] && pageNumber <= chapter.pages[1]) {
      return chapter.nameSimple;
    }
  }
  return '';
}

function formatRelativeDate(due: string): string {
  const now = Date.now();
  const dueTime = new Date(due).getTime();
  const diffMs = dueTime - now;

  if (diffMs <= 0) {
    const overdueDays = Math.floor(-diffMs / 86_400_000);
    if (overdueDays === 0) return 'today';
    if (overdueDays === 1) return '1 day ago';
    return `${overdueDays} days ago`;
  }

  const days = Math.ceil(diffMs / 86_400_000);
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

// --- Surah grouping within a date bucket ---

interface SurahGroup {
  surahName: string;
  pages: number[];
  earliestDue: string;
}

function groupBySurah(
  cards: ScheduledCard[],
  chapters: Chapter[],
): SurahGroup[] {
  const groups = new Map<string, SurahGroup>();

  for (const card of cards) {
    const surahName = getSurahName(card.pageNumber, chapters);
    const existing = groups.get(surahName);
    if (existing) {
      existing.pages.push(card.pageNumber);
      if (
        new Date(card.due).getTime() < new Date(existing.earliestDue).getTime()
      ) {
        existing.earliestDue = card.due;
      }
    } else {
      groups.set(surahName, {
        surahName,
        pages: [card.pageNumber],
        earliestDue: card.due,
      });
    }
  }

  const result = Array.from(groups.values());
  for (const group of result) {
    group.pages.sort((a, b) => a - b);
  }
  result.sort((a, b) => a.pages[0] - b.pages[0]);
  return result;
}

/** Formats sorted page numbers into compact ranges: [2,3,5] → "p.2–3, 5" */
function formatPageRange(pages: number[]): string {
  if (pages.length === 1) return `p.${pages[0]}`;

  const ranges: string[] = [];
  let start = pages[0];
  let end = pages[0];

  for (let i = 1; i < pages.length; i++) {
    if (pages[i] === end + 1) {
      end = pages[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}–${end}`);
      start = pages[i];
      end = pages[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}–${end}`);
  return `p.${ranges.join(', ')}`;
}

// --- Date grouping ---

interface DateGroup {
  label: string;
  sortKey: number;
  cards: ScheduledCard[];
}

function groupByDate(cards: ScheduledCard[]): DateGroup[] {
  const groups = new Map<string, DateGroup>();

  for (const card of cards) {
    const label = formatRelativeDate(card.due);
    const existing = groups.get(label);
    if (existing) {
      existing.cards.push(card);
    } else {
      groups.set(label, {
        label,
        sortKey: new Date(card.due).getTime(),
        cards: [card],
      });
    }
  }

  return Array.from(groups.values()).sort((a, b) => a.sortKey - b.sortKey);
}

function countDueThisWeek(
  dueNow: ScheduledCard[],
  upcoming: ScheduledCard[],
): number {
  const weekFromNow = Date.now() + 7 * 86_400_000;
  let count = dueNow.length;
  for (const card of upcoming) {
    if (new Date(card.due).getTime() <= weekFromNow) count++;
  }
  return count;
}

function pluralPages(n: number): string {
  return `${n} ${n === 1 ? 'page' : 'pages'}`;
}

export function ReviewSchedule({
  dueNow,
  upcoming,
  chapters,
  onNavigate,
}: ReviewScheduleProps) {
  const totalCount = dueNow.length + upcoming.length;
  const dueThisWeek = countDueThisWeek(dueNow, upcoming);
  const upcomingGroups = groupByDate(upcoming);
  const dueNowSurahs = groupBySurah(dueNow, chapters);

  if (totalCount === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No pages reviewed yet
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          Review schedule
        </h2>
        <p className="text-sm text-muted-foreground">
          {pluralPages(totalCount)} · {dueThisWeek} due this week
        </p>
      </div>

      {/* Due now */}
      {dueNowSurahs.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
            Due now · {pluralPages(dueNow.length)}
          </h3>
          <div className="rounded-xl border border-primary/20 bg-primary/5">
            <ul>
              {dueNowSurahs.map((group, i) => (
                <li key={group.pages[0]}>
                  <button
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-3 text-sm transition-colors hover:bg-primary/10 active:bg-primary/10',
                      i < dueNowSurahs.length - 1 &&
                        'border-b border-primary/10',
                    )}
                    onClick={() => onNavigate(group.pages[0])}
                  >
                    <div className="min-w-0 flex-1 text-left">
                      <span className="font-medium">{group.surahName}</span>
                      <span className="text-muted-foreground">
                        {' '}
                        · {formatPageRange(group.pages)}
                      </span>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatRelativeDate(group.earliestDue)}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : (
        <div className="flex items-center gap-3 rounded-xl bg-muted/50 px-4 py-3">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
          <span className="text-sm text-muted-foreground">
            All caught up for today
          </span>
        </div>
      )}

      {/* Upcoming grouped by date, then by surah */}
      {upcomingGroups.map((dateGroup) => {
        const surahGroups = groupBySurah(dateGroup.cards, chapters);
        return (
          <section key={dateGroup.label}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {dateGroup.label} · {pluralPages(dateGroup.cards.length)}
            </h3>
            <div className="rounded-xl border border-border">
              <ul>
                {surahGroups.map((group, i) => (
                  <li key={group.pages[0]}>
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-3 text-sm transition-colors hover:bg-accent active:bg-accent',
                        i < surahGroups.length - 1 && 'border-b border-border',
                      )}
                      onClick={() => onNavigate(group.pages[0])}
                    >
                      <div className="min-w-0 flex-1 text-left">
                        <span className="font-medium">{group.surahName}</span>
                        <span className="text-muted-foreground">
                          {' '}
                          · {formatPageRange(group.pages)}
                        </span>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        );
      })}
    </div>
  );
}
